import { memo } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { ISidebarSectionProps } from '@/@types/layout';
import { cn } from '@/lib/utils';

function SidebarSection({ title, items }: ISidebarSectionProps) {
  const location = useLocation();

  return (
    <div>
      {title !== null && (
        <div className="mb-2 text-[11px] uppercase tracking-[0.5px] text-foreground-muted px-3">
          {title}
        </div>
      )}
      <div className="flex flex-col gap-0.5">
        {items.map(item => {
          const Icon = item.icon;
          const isActive = item.children
            ? location.pathname.startsWith(item.path)
            : location.pathname === item.path;
          const submenuId = `submenu-${item.path.replace(/\//g, '-')}`;

          return (
            <div key={item.path}>
              <NavLink
                to={item.path}
                end={!item.children}
                className={({ isActive: isNavActive }) =>
                  cn(
                    'flex items-center gap-2 py-2 px-3 rounded-md text-sm transition-colors',
                    item.children
                      ? location.pathname.startsWith(item.path)
                        ? 'text-foreground font-medium'
                        : 'text-foreground-muted hover:text-foreground'
                      : isNavActive
                        ? 'bg-sidebar-primary text-sidebar-primary-foreground'
                        : 'text-foreground-muted hover:text-foreground'
                  )
                }
                aria-expanded={item.children ? isActive : undefined}
                aria-haspopup={item.children ? 'true' : undefined}
                aria-controls={item.children && isActive ? submenuId : undefined}
              >
                <Icon className="w-[18px] h-[18px] shrink-0" aria-hidden="true" />
                <span>{item.label}</span>
              </NavLink>
              {item.children && (
                <div
                  id={submenuId}
                  role="group"
                  aria-label={`${item.label} submenu`}
                  className="ml-4 mt-1 flex flex-col gap-0.5"
                >
                  {item.children.map(child => {
                    const ChildIcon = child.icon;
                    return (
                      <NavLink
                        key={child.path}
                        to={child.path}
                        className={({ isActive: isChildActive }) =>
                          cn(
                            'flex items-center gap-2 rounded-md px-3 py-1.5 text-xs font-medium transition-colors',
                            isChildActive
                              ? 'bg-sidebar-primary text-sidebar-primary-foreground'
                              : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                          )
                        }
                      >
                        <ChildIcon size={14} aria-hidden="true" />
                        {child.label}
                      </NavLink>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export const SidebarSectionMemo = memo(SidebarSection);
